/** What the tour preview cannot show, in words (plan §4): the pane says it plainly rather
 *  than drawing a plausible phone that leaves things out.
 *
 *  - Files are not previewable: a staged cover image, meeting-point photo or route file
 *    never reaches the preview (the request is JSON), and a cleared one is not applied —
 *    the phone shows the stored ones.
 *  - Most of the Tour tab never reaches the app's screens: the Android app reads a tour's
 *    name, description, cover image, schedule and itinerary (`TourTemplateDto`), and the
 *    My Trips switch and dates from each booking. A draft change anywhere else is named
 *    by its group, so nobody waits for it to appear.
 *
 *  Pure functions over the draft state, run by the pane when the draft changes — never a
 *  compile. */
import type { DraftState } from '@/features/records/draft/changeSet';
import { parseRecordKey } from '@/features/records/draft/changeSet';
import { isStagedFile } from '@/features/records/fields/widgets/stagedFiles';

import { ACTIVITY, DAY, DAY_KML, SLOT, TOUR_KML } from '../itinerary/model';
import { FILE_PATHS, TOUR_FIELDS } from '../record/catalog';
import { TOUR_RECORD_REGISTRY, TOUR_SECTIONS } from '../record/registry';
import type { PreviewLimit } from '@/components/PreviewLimits/PreviewLimits';

/** Paths that hold a file. */
const FILE_LIKE: ReadonlySet<string> = new Set([...FILE_PATHS, DAY_KML, TOUR_KML]);

/** Tour paths the Android app draws (or that shape what it draws): the My Bookings card's
 *  name, description and dates, whether the trip is in My Bookings at all, the date line's
 *  departures rule — and the itinerary, whose records are drawn whole. */
const DRAWN_TOUR_PATHS: ReadonlySet<string> = new Set([
  'name',
  'description',
  'mytrips_display',
  'start_date',
  'length',
  'length_is_manual',
  'departures',
  'itinerary.days',
]);
const ITINERARY_ENTITIES: ReadonlySet<string> = new Set([DAY, SLOT, ACTIVITY]);

/** Records of a collection, by entity: the group their rows sit in. */
const ROW_GROUP_TITLES: Readonly<Record<string, string>> = {
  alias: 'External IDs',
  tagcat: 'Tag categories',
  pretrip: 'Pre-trip items',
};

/** Titled groups by `section:group`; an untitled group (Basics' first) names its field. */
const GROUP_TITLES: ReadonlyMap<string, string> = new Map(
  TOUR_SECTIONS.flatMap((s) => s.groups.flatMap((g) => (g.title ? [[`${s.id}:${g.id}`, g.title] as const] : []))),
);
const FIELD_LABELS: ReadonlyMap<string, string> = new Map(TOUR_FIELDS.map((f) => [f.key, f.label] as const));

export interface PreviewDraftNotes {
  /** Staged files, by name. */
  staged: string[];
  /** File fields the draft clears. */
  cleared: number;
  /** Groups holding a change the app does not draw, in page order. */
  undrawn: string[];
}

export function previewDraftNotes(state: DraftState): PreviewDraftNotes {
  const staged: string[] = [];
  let cleared = 0;
  const undrawn = new Map<string, number>();
  for (const [rec, entries] of Object.entries(state.entries)) {
    const { entity } = parseRecordKey(rec);
    for (const [path, entry] of Object.entries(entries)) {
      if (FILE_LIKE.has(path)) {
        if (isStagedFile(entry.to)) staged.push(entry.to.name);
        else if (entry.to === null || entry.to === undefined) cleared += 1;
        continue;
      }
      if (ITINERARY_ENTITIES.has(entity) || (entity === 'tour' && DRAWN_TOUR_PATHS.has(path))) continue;
      const title = ROW_GROUP_TITLES[entity] ?? groupTitleOf(path);
      if (title && !undrawn.has(title)) undrawn.set(title, orderOf(path, entity));
    }
  }
  return {
    staged,
    cleared,
    undrawn: [...undrawn.entries()].sort((a, b) => a[1] - b[1]).map(([t]) => t),
  };
}

function groupTitleOf(path: string): string | null {
  const at = TOUR_RECORD_REGISTRY[path];
  if (!at) return null;
  return GROUP_TITLES.get(`${at.section}:${at.group}`) ?? FIELD_LABELS.get(path) ?? null;
}

/** Page order: section, group, then the key's order inside it. */
function orderOf(path: string, entity: string): number {
  const key = ROW_GROUP_TITLES[entity] ? `${entity}.` : path;
  const at = TOUR_RECORD_REGISTRY[key] ?? Object.entries(TOUR_RECORD_REGISTRY).find(([k]) => k.startsWith(key))?.[1];
  if (!at) return Number.MAX_SAFE_INTEGER;
  const section = TOUR_SECTIONS.findIndex((s) => s.id === at.section);
  const group = section >= 0 ? TOUR_SECTIONS[section]!.groups.findIndex((g) => g.id === at.group) : 0;
  return section * 100_000 + group * 1000 + at.order;
}

/** What the preview never shows, whatever the draft holds: what (the lead), and why. */
export type { PreviewLimit };

export const PREVIEW_LIMITS: readonly PreviewLimit[] = [
  {
    lead: 'Meeting point, Content, self-registration, pre-check-in',
    reason: 'no app screen shows them (Use operator pre-check settings included)',
  },
  {
    lead: 'Identifiers, launch policy, access tags, tag categories, chat settings',
    reason: 'they decide who gets the trip and how, not what its screens show',
  },
  { lead: 'Pre-trip items', reason: 'the app lists them behind the trip’s documents button, which is not drawn yet' },
  { lead: 'Staged files and route maps', reason: 'a file you stage is not sent to the preview; KML is never drawn' },
  {
    lead: 'Booking dates',
    reason: 'a tour has no booking of its own — the trip starts today; a fixed-date tour’s My Bookings card uses its start date',
  },
  { lead: 'My Bookings card', reason: 'this trip only — the search box, the “+” button and other bookings are not drawn' },
  { lead: 'iOS', reason: 'this is the Android app; iOS differs in places' },
];
